import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, combineLatest } from "rxjs";
import { debounceTime, filter, map } from "rxjs/operators";
import { CategoryFiltersDTO } from "src/app/features/shared/models/CategoryFilters/CategoryFilters";
import { CloropetConfiguration } from "src/models/CloropetClases";
import { LocalStorageService } from "src/app/features/shared/services/LocalStorage.service";
import { MatDialogRef } from "@angular/material/dialog";
import { LocationCategoryDTO } from "src/app/features/map-features/sections/locations-section/models/LocationCategoryDTO";
import { ProspectsLayerControlService } from "./ProspectsLayerControl.service";
import { HierarchyLevel } from "../../../ui/organisms/polygon-selector-with-levels/models/polygon-selector-with-levels.models";

export interface LayerReloadRequest {
  categoryId: number;
  filters: CategoryFiltersDTO[];
}

@Injectable({
  providedIn: "root",
})
export class ProspectsMapSectionService {
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  agebCloropetConfig: CloropetConfiguration = {
    propertyName: 'TipoZona',
    clases: [
      { classProperty: 'OPORTUNIDAD', classValue: 'rgb(46, 125, 50)' },
      { classProperty: 'TOP', classValue: 'rgb(249, 168, 37)' },
      { classProperty: 'POBLACION', classValue: 'rgb(255, 242, 65)' },
      { classProperty: 'NOMADA', classValue: 'rgb(198, 40, 164)' },
      { classProperty: 'ZONA ROJA', classValue: 'rgb(198, 40, 40)' },
      { classProperty: 'ZONA MAYA', classValue: 'rgb(116, 252, 145)' },
    ]
  };

  private zoneId: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private activeCategory: BehaviorSubject<LocationCategoryDTO> = new BehaviorSubject<LocationCategoryDTO>(null);
  private categoryFiltersSelected: BehaviorSubject<CategoryFiltersDTO[]> = new BehaviorSubject<CategoryFiltersDTO[]>([]);
  private locationCoordsById: Map<string, { lat: number, lng: number }> = new Map();
  private prospectDataChanged: Subject<void> = new Subject<void>();

  activeFilters$: Observable<CategoryFiltersDTO[]> = combineLatest([this.zoneId, this.categoryFiltersSelected]).pipe(
    map(([zoneId, filters]) => zoneId ? [{ Key: 'Zone', Value: zoneId } as CategoryFiltersDTO, ...filters] : null)
  );

  categoryFiltersSelected$: Observable<CategoryFiltersDTO[]> = this.categoryFiltersSelected.asObservable();

  layerReloadTrigger$: Observable<LayerReloadRequest> = combineLatest([this.activeCategory, this.categoryFiltersSelected]).pipe(
    debounceTime(0),
    filter(([category]) => category != null && this.zoneId.value != null),
    map(([category, filters]) => ({
      categoryId: category.id,
      filters: [{ Key: 'Zone', Value: this.zoneId.value } as CategoryFiltersDTO, ...filters]
    }))
  );

  constructor(
    private prospectsLayerControlService: ProspectsLayerControlService,
    private localStorageService: LocalStorageService
  ) {
  }

  setLoading(isLoading: boolean){
    this.isLoading.next(isLoading);
  }

  setIsProspectsVisible(resp: boolean) {
      this.isVisible.next(resp);
  }

  getIsProspectsVisible() {
    return this.isVisible.asObservable();
  }

  setZoneId(zoneId: string): void {
    this.zoneId.next(zoneId);
  }

  getZoneId(): string {
    return this.zoneId.value;
  }

  setCategoryFilters(filters: CategoryFiltersDTO[]): void {
    this.categoryFiltersSelected.next(filters || []);
  }

  getCategoryFilters(): CategoryFiltersDTO[] {
    return this.categoryFiltersSelected.value;
  }

  getCurrentLayerReloadRequest(): LayerReloadRequest {
    const category = this.activeCategory.value;
    const zoneId = this.zoneId.value;
    if (!category || !zoneId) {
      return null;
    }
    return {
      categoryId: category.id,
      filters: [{ Key: 'Zone', Value: zoneId } as CategoryFiltersDTO, ...this.categoryFiltersSelected.value]
    };
  }

  notifyProspectDataChanged(): void {
    this.prospectDataChanged.next();
  }

  getProspectDataChanged(): Observable<void> {
    return this.prospectDataChanged.asObservable();
  }

  resetFilters(): void {
    this.zoneId.next(null);
    this.categoryFiltersSelected.next([]);
    this.locationCoordsById.clear();
  }

  reload(): void {
    this.categoryFiltersSelected.next(this.categoryFiltersSelected.value);
  }

  getProspectCategories(): LocationCategoryDTO[] {
    try {
      const categories = JSON.parse(this.localStorageService.getListaCategorias()) as LocationCategoryDTO[];
      if (!categories) {
        return [];
      }
      return categories.filter(cat => cat.isProspect).map(cat => {
        cat.panelIcon = cat.icon;
        return cat;
      });
    } catch (error) {
      return [];
    }
  }

  setActiveCategory(category: LocationCategoryDTO): void {
    this.activeCategory.next(category);
  }

  getActiveCategory(): LocationCategoryDTO {
    return this.activeCategory.value;
  }

  applyActiveCategoryVisuals(): void {
    const category = this.activeCategory.value;
    if (!category) {
      return;
    }
    this.prospectsLayerControlService.updateCategoryVisuals(category);
  }

  async loadLayers(simpleSelectorPrefixAdd: string): Promise<HierarchyLevel[]> {
    try {
      const hierarchyLevels = await this.prospectsLayerControlService.loadLayersFromAPI(1);
      const defaultCategory = this.getProspectCategories()[0] || null;
      this.activeCategory.next(defaultCategory);
      await this.prospectsLayerControlService.createAllSourcesAndLayers(this.agebCloropetConfig, defaultCategory, simpleSelectorPrefixAdd);
      return hierarchyLevels;
    } catch (error) {
      return [];
    }
  }

  loadLocationsGeoJson(geoJson: any): void {
    if (!geoJson?.features) {
      return;
    }
    this.rebuildLocationCoordsIndex(geoJson);
    this.prospectsLayerControlService.loadMoreLocationGeoJson(geoJson);
  }

  private rebuildLocationCoordsIndex(geoJson: any): void {
    this.locationCoordsById.clear();
    const features = geoJson?.features || [];
    features.forEach((feature: any) => {
      const id = feature?.properties?.['_Id'];
      const coords = feature?.geometry?.coordinates;
      if (id != null && coords?.length === 2) {
        this.locationCoordsById.set(id.toString(), { lat: coords[1], lng: coords[0] });
      }
    });
  }

  getCoordsByIdentifier(identifier: string): { lat: number, lng: number } | null {
    if (identifier == null) {
      return null;
    }
    return this.locationCoordsById.get(identifier.toString()) || null;
  }

  openProspectDetailsPopup(popupData: any): MatDialogRef<any> {
    return this.prospectsLayerControlService.openDetailsPopup(popupData);
  }

  closeProspectDetailsPopup(): void {
    this.prospectsLayerControlService.closeDetailsPopup();
  }

  sendLocationsToTop(): void {
    this.prospectsLayerControlService.sendLocationsToTop();
  }

  clearLocations(): void {
    this.prospectsLayerControlService.clearLocations();
  }

  destroyLayers(): void {
    this.prospectsLayerControlService.destroy();
  }
}
